/*
 * Premium motion — layer 2 of 3: pointer.
 *
 * Mounts card tilt, hover spotlight, click ripples, shine sweeps and rolling
 * nav labels.
 *
 * Buttons deliberately do NOT chase the pointer. An earlier version leaned
 * them toward the cursor, which reads as a wobble: the button moves, the
 * pointer is now somewhere else relative to it, so it moves again. A button
 * that will not hold still is hard to aim at. They now grow a hair on hover
 * and press in on click, and nothing else.
 *
 * The pointer itself is never touched — no custom cursor, no `cursor: none`.
 * The visitor keeps the native arrow and the theme's own `cursor: pointer`
 * hints, the same as on any other site.
 *
 * Every effect writes to the custom properties composed by the single
 * transform declaration in premium-motion.css. Nothing here sets `transform`
 * directly, which is why the press state, tilt, card lift and the scroll reveal
 * can all act on the same element at the same time.
 */
(function () {
  'use strict';

  /*
   * Reduced motion is the only blanket opt-out. Everything else is decided per
   * feature below.
   *
   * This layer deliberately does NOT check `.pm-motion` — that class is the
   * scroll layer's failsafe and dropping it is no reason to lose the hover
   * effects too — and it runs inside the theme editor, because motion nobody
   * can see while previewing is motion nobody can sign off on.
   */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /*
   * `any-hover` / `any-pointer`, NOT `hover` / `pointer`.
   *
   * The unprefixed features describe the PRIMARY pointing device. On a
   * touch-capable Windows laptop — an extremely ordinary way to look at a
   * storefront — the browser reports `pointer: coarse, hover: none` even with
   * a mouse plugged in, which switched this whole layer off. The `any-`
   * variants ask whether ANY attached input can hover and point precisely.
   */
  const CAN_HOVER = window.matchMedia('(any-hover: hover) and (any-pointer: fine)').matches;

  const BUTTONS = [
    '.button',
    '.comparison-section .btn',
    '.button_add_cart',
    '.newsletter-form__button',
    '.shopify-payment-button__button',
  ].join(',');

  /*
   * Tilt leaves a permanent transform on the element, and a transformed
   * ancestor is a containing block for `position: fixed`. That rules out any
   * card that can contain a modal — product cards are tilted on `.card__media`
   * rather than the wrapper so the quick-add modal keeps escaping to the
   * viewport.
   *
   * The FAQ rows, the benefit boxes and the multicolumn cards ("The J1B
   * Solution") are deliberately absent. They are things you read, not things
   * you click through: tilting them and washing them in accent colour on hover
   * made a plain block of text look like it was doing something. All three are
   * left exactly as their own CSS renders them.
   */
  const CARDS = ['.shop-section .product-card', '.card__media', '.article-card-wrapper'].join(',');

  const PRESSABLE = [
    BUTTONS,
    '.header__icon',
    '.icon_sccail',
    '.slider-button',
    '.bannerlatest .content > a',
    '.scroll-indicator',
  ].join(',');

  const SWAP_LINKS = ['.header__menu-item', '.footer-nav a'].join(',');

  const INTERACTIVE = 'a,button,summary,label,select,[role="button"],.swatch,input[type="radio"]';

  /*
   * 4°, down from 7°. A card that tilts hard swings whatever is inside it —
   * including its own add-to-cart button — away from the pointer that is
   * reaching for it, which is the same aiming problem the magnetic buttons
   * had. Shallow enough to read as depth, not as the card dodging you.
   */
  const TILT_MAX_DEG = 4;

  function ensurePositioned(element) {
    if (getComputedStyle(element).position === 'static') element.style.position = 'relative';
  }

  function markTransformable(element) {
    element.setAttribute('data-pm-tx', '');
  }

  /* --------------------------------------------------------------- press */

  /*
   * The whole of the button hover state now lives in CSS — a small scale up on
   * hover, a smaller scale down on press. All this does is opt the element into
   * the shared transform chain so those custom properties are read.
   */
  function mountPress(element) {
    if (element.hasAttribute('data-pm-press')) return;
    element.setAttribute('data-pm-press', '');
    markTransformable(element);
  }

  /* ---------------------------------------------------------------- tilt */

  function mountCard(element) {
    if (element.hasAttribute('data-pm-card')) return;
    element.setAttribute('data-pm-card', '');
    element.setAttribute('data-pm-glow', '');
    markTransformable(element);
    ensurePositioned(element);

    const glow = document.createElement('span');
    glow.className = 'pm-glow';
    glow.setAttribute('aria-hidden', 'true');
    element.appendChild(glow);

    let frameId = null;

    element.addEventListener('pointerenter', () => {
      element.style.setProperty('--pm-persp', '900px');
      element.style.setProperty('--pm-tdur', '220ms');
    });

    element.addEventListener(
      'pointermove',
      (event) => {
        if (frameId) return;
        frameId = window.requestAnimationFrame(() => {
          frameId = null;

          const box = element.getBoundingClientRect();
          const px = (event.clientX - box.left) / box.width;
          const py = (event.clientY - box.top) / box.height;

          element.style.setProperty('--pm-ry', `${((px - 0.5) * TILT_MAX_DEG * 2).toFixed(2)}deg`);
          element.style.setProperty('--pm-rx', `${((0.5 - py) * TILT_MAX_DEG * 2).toFixed(2)}deg`);
          element.style.setProperty('--pm-sx', `${(px * 100).toFixed(1)}%`);
          element.style.setProperty('--pm-sy', `${(py * 100).toFixed(1)}%`);
        });
      },
      { passive: true }
    );

    element.addEventListener('pointerleave', () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = null;

      element.style.setProperty('--pm-tdur', '620ms');
      element.style.setProperty('--pm-rx', '0deg');
      element.style.setProperty('--pm-ry', '0deg');
    });
  }

  /* ------------------------------------------------------- shine + ripple */

  function mountButton(element) {
    if (element.hasAttribute('data-pm-shine')) return;
    element.setAttribute('data-pm-shine', '');
    ensurePositioned(element);

    const shine = document.createElement('span');
    shine.className = 'pm-shine';
    shine.setAttribute('aria-hidden', 'true');
    element.appendChild(shine);

    element.addEventListener('pointerdown', (event) => {
      const box = element.getBoundingClientRect();
      const ripple = document.createElement('span');
      const size = Math.max(box.width, box.height) * 2.4;

      ripple.className = 'pm-ripple';
      ripple.setAttribute('aria-hidden', 'true');
      ripple.style.width = `${size}px`;
      ripple.style.height = `${size}px`;
      ripple.style.left = `${event.clientX - box.left}px`;
      ripple.style.top = `${event.clientY - box.top}px`;

      // Into the shine layer, not the button: that is the element with
      // `overflow: hidden` and the inherited radius, so the ripple stays
      // inside the button's shape instead of spilling past its corners.
      shine.appendChild(ripple);
      ripple.addEventListener('animationend', () => ripple.remove());
    });

    mountPress(element);
  }

  /* ------------------------------------------------------------ nav swap */

  /*
   * toggleSupply's hover-reveal menu. The clone carries the original markup so
   * accent spans and active-state classes keep their styling; it is hidden from
   * assistive tech since it is a duplicate of text already in the tree.
   */
  function mountSwap(link) {
    if (link.hasAttribute('data-pm-swap')) return;
    if (link.querySelector('svg, img, .pm-swap')) return;

    const label = link.textContent.trim();
    if (!label) return;

    link.setAttribute('data-pm-swap', '');
    link.classList.add('pm-swap-link');

    const swap = document.createElement('span');
    swap.className = 'pm-swap';

    const incoming = document.createElement('span');
    incoming.className = 'pm-swap__in';
    incoming.innerHTML = link.innerHTML;

    const outgoing = document.createElement('span');
    outgoing.className = 'pm-swap__out';
    outgoing.setAttribute('aria-hidden', 'true');
    outgoing.innerHTML = link.innerHTML;

    swap.appendChild(incoming);
    swap.appendChild(outgoing);

    link.innerHTML = '';
    link.appendChild(swap);
  }

  /* ------------------------------------------------------------- mounting */

  function mount(scope) {
    scope.querySelectorAll(CARDS).forEach(mountCard);
    scope.querySelectorAll(BUTTONS).forEach(mountButton);
    scope.querySelectorAll(PRESSABLE).forEach(mountPress);
    scope.querySelectorAll(SWAP_LINKS).forEach(mountSwap);
  }

  function init() {
    if (!CAN_HOVER) return;

    mount(document);

    // The editor swaps whole sections in and out.
    document.addEventListener('shopify:section:load', (event) => mount(event.target));

    // Quick add, cart re-renders, facet updates and infinite scroll all inject
    // markup after load.
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.classList && node.classList.contains('pm-ripple')) return;
          mount(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
